import { getDocument } from 'pdfjs-dist';
import { getMemberAccessToken } from '../auth/supabase';
import { downloadNativeIap, nativeIapAvailable } from './nativeIap';

function contentDispositionFilename(value) {
  const disposition = String(value || '');
  const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";\r\n]+)/i);
  if (!match?.[1]) return '';
  try {
    return decodeURIComponent(match[1].replace(/^"|"$/g, ''));
  } catch (_) {
    return match[1].replace(/^\"|\"$/g, '');
  }
}

async function extractPdfText(blob) {
  const bytes = await blob.arrayBuffer();
  const loadingTask = getDocument({ data: bytes });
  const pdf = await loadingTask.promise;
  const textPages = [];
  let budget = 1500000;
  let textTruncated = false;

  try {
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      if (budget <= 0) {
        textPages.push('');
        textTruncated = true;
        continue;
      }
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();
      const raw = (content.items || []).map(item => String(item?.str || '')).join(' ').replace(/\s+/g, ' ').trim();
      const allowed = Math.min(raw.length, budget, 40000);
      if (allowed < raw.length) textTruncated = true;
      const clipped = raw.slice(0, allowed);
      textPages.push(clipped);
      budget -= clipped.length;
    }
    return { pageCount: pdf.numPages, textPages, textTruncated };
  } finally {
    try { await pdf.destroy(); } catch (_) {}
  }
}

function randomId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return 'iap-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
}

export async function downloadSelectedIap(documentUrl) {
  if (nativeIapAvailable()) {
    const native = await downloadNativeIap(documentUrl);
    if (!native?.id || !native?.fileUri) throw new Error('The iPhone did not return a saved IAP file.');
    return { ...native, blob: null, storage: 'native' };
  }

  const token = await getMemberAccessToken();
  if (!token) throw new Error('Member sign in required.');

  const response = await fetch('/api/iap-pdf?src=' + encodeURIComponent(documentUrl), {
    cache: 'no-store',
    headers: { Authorization: 'Bearer ' + token },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || 'Unable to download this IAP.');
  }

  const blob = await response.blob();
  if (!String(blob.type || '').toLowerCase().includes('pdf')) throw new Error('The selected item is not a PDF.');
  const extracted = await extractPdfText(blob);
  const filename = contentDispositionFilename(response.headers.get('content-disposition')) || 'Incident-IAP.pdf';
  return {
    id: randomId(),
    fileUri: '',
    filename,
    originalUrl: String(documentUrl || ''),
    bytes: blob.size,
    pageCount: extracted.pageCount,
    textPages: extracted.textPages,
    textTruncated: extracted.textTruncated,
    blob,
    storage: 'indexeddb',
  };
}
